import { useTranslations } from 'next-intl'
import { useCallback, useMemo } from 'react'
import useClevertapForSrp from '../../analytics/clevertap/srp'
import useUpdateSearchResultCache from '../updateSearchResultCache'
import useSearchResults from '../useSearchResults'

export interface LayoverAirportOption {
    id: string
    label: string
    isSelected: boolean
}

const useLayoverAirportFilterManager = (journeyLeg: 0 | 1 = 0) => {
    const t = useTranslations('common')
    const { triggerSrpEvent } = useClevertapForSrp()
    const updateCache = useUpdateSearchResultCache()
    const { filterData, jsons, selectedJourneyLeg } = useSearchResults(journeyLeg)
    const targetLayoverAirport = filterData?.layover_Airport?.[selectedJourneyLeg]
    const selectedOptions = targetLayoverAirport?.selectedOptions || []
    const selectedOptionSet = useMemo(() => new Set(selectedOptions.map(value => value?.toUpperCase())), [selectedOptions])
    const layoverValues = targetLayoverAirport?.values || []
    const airportsJson = jsons?.ap || {}
    const title = t('layoverAirport')

    const layoverAirportOptions = useMemo(() => {
        return layoverValues
            .map(value => {
                const airportCode = value?.toUpperCase()
                const airport = airportsJson[airportCode]
                const label = airport ? `${airport.n}, ${airport.c}` : airportCode
                return {
                    id: airportCode,
                    label,
                    isSelected: selectedOptionSet.has(airportCode)
                }
            })
            .sort((a, b) => a.label.localeCompare(b.label))
    }, [airportsJson, layoverValues, selectedOptionSet])

    const handleSelectToggle = useCallback(
        (id: string) => {
            const isRemoving = selectedOptions.includes(id)
            const selectedValues = isRemoving
                ? selectedOptions.filter(value => value !== id)
                : [...selectedOptions, id]

            triggerSrpEvent('Air_UI_Action', {
                action_name: 'filter',
                action_type: 'layover_airport',
                action_value: selectedValues.join(',') || 'none'
            })

            updateCache(currentData => {
                if (currentData?.filterData?.layover_Airport?.[selectedJourneyLeg]) {
                    currentData.filterData.layover_Airport[selectedJourneyLeg].selectedOptions = selectedValues
                }
                return currentData as NonNullable<typeof currentData>
            })
        },
        [selectedOptions, selectedJourneyLeg, triggerSrpEvent, updateCache]
    )

    const handleReset = useCallback(() => {
        triggerSrpEvent('Air_UI_Action', {
            action_name: 'filter',
            action_type: 'reset',
            action_value: 'layover_airport'
        })

        updateCache(currentData => {
            if (currentData?.filterData?.layover_Airport?.[selectedJourneyLeg]) {
                currentData.filterData.layover_Airport[selectedJourneyLeg].selectedOptions = []
            }
            return currentData as NonNullable<typeof currentData>
        })
    }, [selectedJourneyLeg, triggerSrpEvent, updateCache])

    const shouldShowReset = useMemo(() => {
        return selectedOptions.length > 0
    }, [selectedOptions])

    return {
        title,
        layoverAirportOptions,
        handleSelectToggle,
        handleReset,
        shouldShowReset
    }
}

export default useLayoverAirportFilterManager
